/**
 * The Joining card's three save-on-toggle rows: `Approve new members
 * myself`, `Let members invite others`, `Anyone can find this pool`.
 * `issue.md` R2 and R6. Each row writes exactly one `/pool/update` field
 * (`joiningCardState.ts`'s `buildJoiningUpdateBody`) and shows its own
 * persistent inline `Saved`, with no other outcome mechanism. The three
 * `checked` values come from `plan.md ## 5.17`. The control is `Checkbox`,
 * not a switch:
 * `@sportsmet/ui` has no switch primitive and `Checkbox` is what `apps/app`
 * already uses for this exact save-on-toggle shape (`plan.md ## 6.5`).
 */
import { usePoolUpdate } from "@sportsmet/pool-sdk/private";
import { Checkbox, SavedNote } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  buildJoiningUpdateBody,
  resolveJoiningToggles,
  type typeJoiningPoolInput,
  type typeJoiningToggleField,
} from "./joiningCardState";

export type typePoolSettingsJoiningTogglesProps = {
  poolId: string;
  pool: typeJoiningPoolInput;
  isPoolLoading: boolean;
};

export const PoolSettingsJoiningToggles = ({
  poolId,
  pool,
  isPoolLoading,
}: typePoolSettingsJoiningTogglesProps): ReactNode => {
  const update = usePoolUpdate();
  const [savedField, setSavedField] = useState<typeJoiningToggleField | null>(
    null,
  );
  const [pending, setPending] = useState<
    Record<typeJoiningToggleField, boolean | null>
  >({ approveMyself: null, memberSharing: null, publicListing: null });

  async function handleChange(
    field: typeJoiningToggleField,
    next: boolean,
  ): Promise<void> {
    setPending((current) => ({ ...current, [field]: next }));
    setSavedField(null);
    try {
      const result = await update.mutateAsync({
        poolId,
        ...buildJoiningUpdateBody(field, next),
      });
      if (!showApiError(result)) setSavedField(field);
      setPending((current) => ({ ...current, [field]: null }));
    } catch {
      showOfflineError();
      setPending((current) => ({ ...current, [field]: null }));
    }
  }

  const rows = resolveJoiningToggles(pool);

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="pool-settings-joining-toggles"
    >
      {rows.map((row) => (
        <div
          key={row.field}
          className="flex flex-col gap-1"
          data-testid={`pool-settings-joining-toggle-${row.field}`}
        >
          <Checkbox
            label={row.label}
            description={row.description}
            checked={pending[row.field] ?? row.checked}
            disabled={isPoolLoading}
            onCheckedChange={(next) => {
              void handleChange(row.field, next === true);
            }}
          />
          <div data-testid={`pool-settings-joining-saved-${row.field}`}>
            <SavedNote saved={savedField === row.field} />
          </div>
        </div>
      ))}
    </div>
  );
};
